import { z } from 'zod'
import { webhookDeliveryStatusSchema } from '../enums/webhook-delivery-status.enum.js'
import { webhookEventNameSchema } from '../enums/webhook-event.enum.js'
import { jsonValueSchema } from '../helpers/json.js'
import { uuidSchema } from '../validators/params.js'

/** One webhook delivery as the dashboard's log lists it. Its `id` is the event id the receiver sees. */
export const webhookDeliveryDtoSchema = z.object({
  id: uuidSchema,
  event: webhookEventNameSchema,
  /** Null for organization-level events (`billing.*`), which reference no envelope. */
  envelopeId: uuidSchema.nullable(),
  signerId: uuidSchema.nullable(),
  url: z.string(),
  status: webhookDeliveryStatusSchema,
  attemptCount: z.number().int().nonnegative(),
  lastAttemptAt: z.string().datetime().nullable(),
  lastStatusCode: z.number().int().nullable(),
  lastError: z.string().nullable(),
  deliveredAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
})

export type WebhookDeliveryDto = z.infer<typeof webhookDeliveryDtoSchema>

export const webhookDeliveryListDtoSchema = z.object({
  deliveries: z.array(webhookDeliveryDtoSchema),
  /** Pass as `before` for the next (older) page; null when there are no more. */
  nextBefore: z.string().nullable(),
})

export type WebhookDeliveryListDto = z.infer<typeof webhookDeliveryListDtoSchema>

/** One delivery with the exact payload that was (or will be) sent. */
export const webhookDeliveryDetailDtoSchema = webhookDeliveryDtoSchema.extend({
  payload: z.record(z.string(), jsonValueSchema),
})

export type WebhookDeliveryDetailDto = z.infer<typeof webhookDeliveryDetailDtoSchema>

/** What happened when a synthetic test event was sent to the test webhook URL. */
export const webhookTestResultDtoSchema = z.object({
  event: webhookEventNameSchema,
  eventId: uuidSchema,
  url: z.string(),
  delivered: z.boolean(),
  statusCode: z.number().int().nullable(),
  error: z.string().nullable(),
  durationMs: z.number().int().nonnegative(),
  /** The start of the receiver's response body. */
  responseExcerpt: z.string().nullable(),
  payload: z.record(z.string(), jsonValueSchema),
})

export type WebhookTestResultDto = z.infer<typeof webhookTestResultDtoSchema>
